import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSearch } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LineChart, Line, ResponsiveContainer, Tooltip } from "recharts";
import {
  ExternalLink, RefreshCw, Lock, AlertCircle, ChevronUp, ChevronDown,
  Minus, Play, Newspaper, FlaskConical, Mic2, TrendingUp, Video,
  Search, Zap, BookOpen, Loader2, Shield, BarChart, CheckCircle,
  XCircle, ClipboardList, FileText, Sparkles, Info,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────
interface NewsSource {
  source: string;
  items: { title: string; link: string; pubDate: string; summary: string }[];
}
interface Paper {
  term: string; id: string; title: string; pubDate: string; journal: string; authors: string; link: string;
}
interface YoutubeChannel {
  channel: string;
  videos: { videoId: string; title: string; publishedAt: string; thumbnail: string; url: string }[];
}
interface TermData {
  term: string; composite: number; currentRss: number; currentReddit: number;
  isTrending: boolean; trendDirection: "up" | "down" | "stable";
  sparkline: number[]; oneliner?: string;
  trendingSources?: { title: string; url: string }[];
  flaggedAt?: number; lastUpdated: number;
}
interface TrendingResponse { trending: TermData[]; allTerms: TermData[]; }
interface LookupResult {
  term: string; synthesis: string;
  citations: { id: string; title: string; journal: string; year: string; url: string }[];
  furtherReading: { label: string; url: string }[];
  disclaimer: string; fromCache: boolean;
}
interface RegulatoryItem {
  title: string; link: string; pubDate: string; source: string;
  type: string; compounds: string[];
}
interface BriefingResponse {
  lines: string[]; generatedAt: number; cachedUntil: number; fromCache: boolean;
}
type StrategicRating = "High" | "Medium" | "Low" | "Not a fit";
type RatedDimension = { rating: StrategicRating; rationale: string };
interface MarketingBriefResult {
  term: string; mechanism: string; targetAudience: string;
  compliantClaims: string[]; avoidClaims: string[];
  positioningAngle: string; demandSignal: string;
  competitorMessagingThemes: string[]; commonlyBundledWith: string[];
  strategicFit: {
    patientProblem: string;
    retentionPlay: RatedDimension;
    acquisitionPlay: RatedDimension;
    aovImpact: RatedDimension;
    ltvImpact: RatedDimension;
    competitiveDefense: RatedDimension;
    brandAwareness: RatedDimension;
    newCustomerSegment: RatedDimension & { segmentDescription: string };
    strategicSummary: string;
  };
  generatedAt: string; fromCache: boolean;
}
interface ScorecardRow {
  compound: string; agelessrxLive: boolean;
  regulatoryStatus: "green" | "yellow" | "red";
  regulatoryNote: string; researchSignal: number | null;
}

// ─── API ──────────────────────────────────────────────────────────────────────
const API = "/api/peptides";

function usePeptideQuery<T>(endpoint: string, key: string, enabled = true) {
  return useQuery<T>({
    queryKey: ["peptides", endpoint, key],
    queryFn: async () => {
      const res = await fetch(`${API}/${endpoint}`, { headers: { "x-peptide-key": key } });
      if (res.status === 401) throw new Error("UNAUTHORIZED");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json() as Promise<T>;
    },
    enabled,
    staleTime: 40 * 60 * 1000,
    retry: (n, err) => err instanceof Error && err.message === "UNAUTHORIZED" ? false : n < 2,
  });
}

// ─── Shared UI ────────────────────────────────────────────────────────────────
function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse bg-slate-200 dark:bg-slate-700 rounded ${className}`} />;
}
function CardSkeleton() {
  return (
    <div className="space-y-2 p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700">
      <Skeleton className="h-4 w-3/4" /><Skeleton className="h-3 w-1/2" /><Skeleton className="h-3 w-full" />
    </div>
  );
}
function Section({
  title, icon: Icon, isLoading, error, children, className = "",
}: {
  title: string; icon: React.ElementType; isLoading?: boolean; error?: Error | null; children: React.ReactNode; className?: string;
}) {
  return (
    <div className={`bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden ${className}`}>
      <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60">
        <Icon className="w-4 h-4 text-slate-500" />
        <h2 className="font-mono text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">{title}</h2>
        {isLoading && <RefreshCw className="w-3 h-3 text-slate-400 animate-spin ml-auto" />}
      </div>
      <div className="p-4">
        {error && error.message !== "UNAUTHORIZED" && (
          <div className="flex items-start gap-2 text-amber-600 dark:text-amber-400 text-sm p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /><span>Source unavailable — {error.message}</span>
          </div>
        )}
        {!error && children}
      </div>
    </div>
  );
}

// ─── Sparkline ────────────────────────────────────────────────────────────────
function Sparkline({ data, color = "#3b82f6" }: { data: number[]; color?: string }) {
  if (!data.length) return <div className="w-20 h-8 bg-slate-100 dark:bg-slate-700 rounded" />;
  const chartData = data.map((v, i) => ({ i, v }));
  return (
    <ResponsiveContainer width={80} height={32}>
      <LineChart data={chartData} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
        <Line type="monotone" dataKey="v" stroke={color} dot={false} strokeWidth={1.5} isAnimationActive={false} />
        <Tooltip contentStyle={{ display: "none" }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── Trending Section ─────────────────────────────────────────────────────────
function signalLevel(composite: number, isTrending: boolean): { label: string; color: string; bg: string; bar: string } {
  if (isTrending)           return { label: "Spiking",  color: "text-orange-700 dark:text-orange-400", bg: "bg-orange-100 dark:bg-orange-900/40", bar: "bg-orange-500" };
  if (composite >= 0.75)    return { label: "High",     color: "text-emerald-700 dark:text-emerald-400", bg: "bg-emerald-100 dark:bg-emerald-900/40", bar: "bg-emerald-500" };
  if (composite >= 0.40)    return { label: "Moderate", color: "text-blue-700 dark:text-blue-400", bg: "bg-blue-100 dark:bg-blue-900/40", bar: "bg-blue-400" };
  if (composite >= 0.10)    return { label: "Low",      color: "text-slate-600 dark:text-slate-400", bg: "bg-slate-100 dark:bg-slate-700", bar: "bg-slate-400" };
  return                           { label: "Minimal",  color: "text-slate-400 dark:text-slate-500", bg: "bg-slate-100 dark:bg-slate-700/50", bar: "bg-slate-300" };
}

function TrendingSection({ pKey }: { pKey: string }) {
  const { data, isLoading, error } = usePeptideQuery<TrendingResponse>("trending", pKey, !!pKey);
  const [legendOpen, setLegendOpen] = React.useState(false);

  const trendingNow = data?.trending ?? [];
  const allTerms = data?.allTerms ?? [];

  const TrendArrow = ({ dir }: { dir: "up" | "down" | "stable" }) =>
    dir === "up" ? <ChevronUp className="w-3.5 h-3.5 text-green-500" /> :
    dir === "down" ? <ChevronDown className="w-3.5 h-3.5 text-red-400" /> :
    <Minus className="w-3.5 h-3.5 text-slate-400" />;

  return (
    <div className="space-y-4">
      {/* Trending Now Strip */}
      {(isLoading || trendingNow.length > 0) && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-orange-200 dark:border-orange-800 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-orange-100 dark:border-orange-900 bg-orange-50 dark:bg-orange-900/30">
            <Zap className="w-4 h-4 text-orange-500" />
            <h2 className="font-mono text-xs font-semibold uppercase tracking-widest text-orange-600 dark:text-orange-400">Trending Now</h2>
            {isLoading && <RefreshCw className="w-3 h-3 text-orange-400 animate-spin ml-auto" />}
          </div>
          <div className="p-4">
            {isLoading ? (
              <div className="flex gap-3">{[1, 2].map(i => <CardSkeleton key={i} />)}</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {trendingNow.map((term) => (
                  <div key={term.term} className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <span className="font-mono font-bold text-sm text-slate-800 dark:text-slate-100">{term.term}</span>
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400 rounded-full font-mono uppercase tracking-wider">trending</span>
                      </div>
                      <Sparkline data={term.sparkline} color="#f97316" />
                    </div>
                    {term.oneliner && (
                      <p className="text-xs text-slate-600 dark:text-slate-400 italic leading-relaxed mb-2">"{term.oneliner}"</p>
                    )}
                    {term.trendingSources && term.trendingSources.length > 0 && (
                      <div className="space-y-0.5">
                        {term.trendingSources.slice(0, 2).map((src, i) => (
                          <a key={i} href={src.url} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 truncate">
                            <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                            <span className="truncate">{src.title}</span>
                          </a>
                        ))}
                      </div>
                    )}
                    <p className="text-[10px] text-slate-400 mt-2">
                      14-day papers: {term.currentRss} · 90-day base: {term.currentReddit}
                      {term.flaggedAt && ` · flagged ${new Date(term.flaggedAt).toLocaleTimeString()}`}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* All Terms Table */}
      <Section title="Signal Tracker — All Terms" icon={TrendingUp} isLoading={isLoading} error={error as Error}>
        {isLoading ? (
          <div className="space-y-2">{[1, 2, 3, 4].map(i => <CardSkeleton key={i} />)}</div>
        ) : allTerms.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-400">
            <RefreshCw className="w-6 h-6 mx-auto mb-2 text-slate-300 animate-spin" />
            First poll in progress — check back in ~30 seconds.
          </div>
        ) : (
          <div className="space-y-4">

            {/* Legend / explainer */}
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <button
                onClick={() => setLegendOpen(o => !o)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 dark:bg-slate-700/40 text-left hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Info className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs font-mono font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">How to read this table</span>
                </div>
                {legendOpen
                  ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                  : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
              </button>

              {legendOpen && (
                <div className="px-4 py-4 bg-white dark:bg-slate-800 space-y-4 text-xs text-slate-600 dark:text-slate-400">
                  <p className="leading-relaxed">
                    Each row tracks a compound's <span className="font-semibold text-slate-700 dark:text-slate-300">academic research activity</span> on PubMed.
                    The goal is to surface which compounds are getting increased scientific attention — a leading indicator of market interest, new clinical applications, or emerging regulatory scrutiny.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <p className="font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-[10px]">Columns</p>
                      <div className="space-y-1.5">
                        <div className="flex gap-2"><span className="font-mono font-semibold text-slate-700 dark:text-slate-300 shrink-0">Signal</span><span>How active the research base is right now — derived from paper volume, not a raw score.</span></div>
                        <div className="flex gap-2"><span className="font-mono font-semibold text-slate-700 dark:text-slate-300 shrink-0">Δ</span><span>Direction since the last poll (30 min). Stable is normal — changes only appear when paper counts shift.</span></div>
                        <div className="flex gap-2"><span className="font-mono font-semibold text-slate-700 dark:text-slate-300 shrink-0">New (14d)</span><span>Papers published in the last 14 days. A spike here while the 90d base is low = emerging compound.</span></div>
                        <div className="flex gap-2"><span className="font-mono font-semibold text-slate-700 dark:text-slate-300 shrink-0">Base (90d)</span><span>Total papers in the last 90 days. High = established research body. Low = niche or novel.</span></div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-[10px]">Signal Levels</p>
                      <div className="space-y-1.5">
                        {([
                          { label: "Spiking",  bg: "bg-orange-100 dark:bg-orange-900/40", color: "text-orange-700 dark:text-orange-400", desc: "14-day count is unusually high vs. baseline — something is driving new interest." },
                          { label: "High",     bg: "bg-emerald-100 dark:bg-emerald-900/40", color: "text-emerald-700 dark:text-emerald-400", desc: "Strong, sustained publication rate. Well-studied compound." },
                          { label: "Moderate", bg: "bg-blue-100 dark:bg-blue-900/40", color: "text-blue-700 dark:text-blue-400", desc: "Active but not dominant. Growing or established niche." },
                          { label: "Low",      bg: "bg-slate-100 dark:bg-slate-700", color: "text-slate-600 dark:text-slate-400", desc: "Limited recent publications. Early-stage or declining interest." },
                          { label: "Minimal",  bg: "bg-slate-100 dark:bg-slate-700/50", color: "text-slate-400 dark:text-slate-500", desc: "Very few or no papers found. Novel, obscure, or not indexed." },
                        ] as const).map(({ label, bg, color, desc }) => (
                          <div key={label} className="flex items-start gap-2">
                            <span className={`shrink-0 text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded ${bg} ${color}`}>{label}</span>
                            <span>{desc}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <p className="text-[11px] text-slate-400 border-t border-slate-100 dark:border-slate-700 pt-3">
                    Data source: PubMed / NCBI E-utilities API. Polls every 30 minutes. Research activity is not a proxy for safety, efficacy, or regulatory approval.
                  </p>
                </div>
              )}
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-slate-700">
                    <th className="text-left pb-2 text-xs font-mono text-slate-400 uppercase tracking-wider">Compound</th>
                    <th className="text-left pb-2 text-xs font-mono text-slate-400 uppercase tracking-wider">Signal</th>
                    <th className="text-center pb-2 text-xs font-mono text-slate-400 uppercase tracking-wider">Δ</th>
                    <th className="text-center pb-2 text-xs font-mono text-slate-400 uppercase tracking-wider hidden sm:table-cell">New (14d)</th>
                    <th className="text-center pb-2 text-xs font-mono text-slate-400 uppercase tracking-wider hidden sm:table-cell">Base (90d)</th>
                    <th className="text-right pb-2 text-xs font-mono text-slate-400 uppercase tracking-wider">History</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 dark:divide-slate-700/50">
                  {allTerms
                    .slice()
                    .sort((a, b) => b.composite - a.composite)
                    .map((term) => {
                      const sig = signalLevel(term.composite, term.isTrending);
                      return (
                        <tr key={term.term} className={`group hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors ${term.isTrending ? "bg-orange-50/50 dark:bg-orange-900/10" : ""}`}>
                          <td className="py-2.5 pr-3">
                            <div className="flex items-center gap-1.5">
                              {term.isTrending && <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse shrink-0" />}
                              <span className={`font-medium ${term.isTrending ? "text-orange-700 dark:text-orange-400" : "text-slate-700 dark:text-slate-300"}`}>{term.term}</span>
                            </div>
                          </td>
                          <td className="py-2.5 pr-3">
                            <div className="flex items-center gap-2">
                              <span className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded ${sig.bg} ${sig.color}`}>{sig.label}</span>
                              <div className="hidden md:block w-16 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                                <div className={`h-full rounded-full ${sig.bar}`} style={{ width: `${Math.round(term.composite * 100)}%` }} />
                              </div>
                            </div>
                          </td>
                          <td className="py-2.5 text-center">
                            <div className="flex justify-center"><TrendArrow dir={term.trendDirection} /></div>
                          </td>
                          <td className="py-2.5 text-center hidden sm:table-cell">
                            <span className={`text-xs font-mono ${term.currentRss > 0 ? "text-slate-700 dark:text-slate-300 font-semibold" : "text-slate-400"}`}>
                              {term.currentRss > 0 ? `+${term.currentRss}` : "—"}
                            </span>
                          </td>
                          <td className="py-2.5 text-center text-xs text-slate-400 hidden sm:table-cell font-mono">{term.currentReddit || "—"}</td>
                          <td className="py-2.5 text-right">
                            <div className="flex justify-end"><Sparkline data={term.sparkline} color={term.isTrending ? "#f97316" : sig.label === "High" ? "#10b981" : "#94a3b8"} /></div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
              {allTerms[0]?.lastUpdated && (
                <p className="text-[11px] text-slate-400 mt-3 text-right">
                  Last polled: {new Date(allTerms[0].lastUpdated).toLocaleTimeString()} · updates every 30 min
                </p>
              )}
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}

// ─── Lookup Section ───────────────────────────────────────────────────────────
function renderSynthesis(text: string): React.ReactNode {
  return text.split("\n").map((line, i) => {
    const boldHeader = line.match(/^\*\*(.+?)\*\*/);
    if (boldHeader) {
      const rest = line.replace(/^\*\*(.+?)\*\*/, "").replace(/^:\s*/, "").trim();
      return (
        <div key={i} className="mt-4 first:mt-0">
          <h4 className="font-semibold text-slate-800 dark:text-slate-200 text-sm mb-1">{boldHeader[1]}</h4>
          {rest && <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{renderInlineBold(rest)}</p>}
        </div>
      );
    }
    if (line.startsWith("- ")) {
      return (
        <li key={i} className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed ml-4 list-disc">
          {renderInlineBold(line.slice(2))}
        </li>
      );
    }
    if (line.trim() === "") return null;
    return <p key={i} className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{renderInlineBold(line)}</p>;
  });
}

function renderInlineBold(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|\[\d+\])/g);
  return parts.map((part, i) => {
    if (part.match(/^\*\*[^*]+\*\*$/)) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.match(/^\[\d+\]$/)) return <sup key={i} className="text-blue-500 font-mono text-[10px]">{part}</sup>;
    return part;
  });
}

const QUICK_TERMS = ["BPC-157", "TB-500", "GHK-Cu", "Ipamorelin", "Semax", "Epithalon"];

function LookupSection({ pKey }: { pKey: string }) {
  const [activeTab, setActiveTab] = useState<"research" | "marketing">("research");
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");

  // Research tab state
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<LookupResult | null>(null);
  const [error, setError] = useState("");

  // Marketing Brief tab state
  const [mktLoading, setMktLoading] = useState(false);
  const [mktResult, setMktResult] = useState<MarketingBriefResult | null>(null);
  const [mktError, setMktError] = useState("");

  const doLookup = useCallback(async (term: string) => {
    if (!term.trim()) return;
    const q = term.trim();
    setSubmitted(q); setQuery(q); setIsLoading(true); setError(""); setResult(null);
    try {
      const res = await fetch(`${API}/lookup?q=${encodeURIComponent(q)}`, { headers: { "x-peptide-key": pKey } });
      if (res.status === 401) { setError("Auth error — reload and re-enter your key."); return; }
      if (!res.ok) { setError(`Server error (${res.status})`); return; }
      setResult((await res.json()) as LookupResult);
    } catch { setError("Network error — check your connection."); }
    finally { setIsLoading(false); }
  }, [pKey]);

  const doMarketingBrief = useCallback(async (term: string) => {
    if (!term.trim()) return;
    const q = term.trim();
    setSubmitted(q); setQuery(q); setMktLoading(true); setMktError(""); setMktResult(null);
    try {
      const res = await fetch(`${API}/marketing-brief?q=${encodeURIComponent(q)}`, { headers: { "x-peptide-key": pKey } });
      if (res.status === 401) { setMktError("Auth error — reload and re-enter your key."); return; }
      if (!res.ok) { setMktError(`Server error (${res.status})`); return; }
      setMktResult((await res.json()) as MarketingBriefResult);
    } catch { setMktError("Network error — check your connection."); }
    finally { setMktLoading(false); }
  }, [pKey]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (activeTab === "research") doLookup(query);
    else doMarketingBrief(query);
  }

  function handleQuickPick(term: string) {
    if (activeTab === "research") doLookup(term);
    else doMarketingBrief(term);
  }

  const loading = activeTab === "research" ? isLoading : mktLoading;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60">
        <BookOpen className="w-4 h-4 text-slate-500" />
        <h2 className="font-mono text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">Peptide Wiki</h2>
        <span className="ml-auto text-[10px] text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full font-mono">LLM · PubMed · cached 24h</span>
      </div>

      {/* Tab switcher */}
      <div className="flex border-b border-slate-100 dark:border-slate-700">
        {(["research", "marketing"] as const).map((tab) => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors
              ${activeTab === tab
                ? "border-b-2 border-slate-900 dark:border-white text-slate-900 dark:text-white -mb-px"
                : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"}`}>
            {tab === "research" ? <><FlaskConical className="w-3.5 h-3.5" /> Research Brief</> : <><FileText className="w-3.5 h-3.5" /> Marketing Brief</>}
          </button>
        ))}
      </div>

      <div className="p-5">
        {/* Disclaimer */}
        <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg mb-4">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
          <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
            {activeTab === "research"
              ? <><strong>Research use only.</strong> Summaries are synthesized from PubMed literature and are not medical advice.</>
              : <><strong>Drafting aid only.</strong> Marketing Brief is AI-generated and must be reviewed for compliance. Not legal or regulatory advice — all claims require compliance review before use.</>}
          </p>
        </div>

        {/* Search */}
        <form onSubmit={handleSubmit} className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. BPC-157, Semax, Epithalon…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:border-blue-400 dark:focus:border-blue-500" />
          </div>
          <button type="submit" disabled={loading || !query.trim()}
            className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-medium rounded-lg hover:bg-slate-700 dark:hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Look up"}
          </button>
        </form>

        {/* Quick-pick chips */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {QUICK_TERMS.map((t) => (
            <button key={t} onClick={() => handleQuickPick(t)}
              className="text-xs px-2.5 py-1 rounded-full border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors">
              {t}
            </button>
          ))}
        </div>

        {/* ── Research Tab ── */}
        {activeTab === "research" && (
          <>
            {isLoading && (
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Searching PubMed and synthesizing with AI for "{submitted}"…
                </div>
                <Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-3/4" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-2/3" />
              </div>
            )}
            {error && !isLoading && (
              <div className="flex items-start gap-2 text-red-600 dark:text-red-400 text-sm p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /><span>{error}</span>
              </div>
            )}
            {result && !isLoading && (
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-slate-900 dark:text-white">{result.term}</h3>
                  {result.fromCache && <span className="text-[10px] font-mono text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full">from cache</span>}
                </div>
                <div className="prose-like space-y-1">{renderSynthesis(result.synthesis)}</div>
                {result.citations.length > 0 && (
                  <div className="border-t border-slate-100 dark:border-slate-700 pt-4">
                    <h4 className="text-xs font-mono font-semibold uppercase tracking-wider text-slate-400 mb-2">Sources</h4>
                    <ol className="space-y-1.5">
                      {result.citations.map((c, i) => (
                        <li key={c.id} className="flex items-start gap-2">
                          <span className="text-[10px] font-mono text-blue-500 mt-0.5 shrink-0">[{i + 1}]</span>
                          <a href={c.url} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 leading-snug">
                            {c.title || `PubMed ${c.id}`} — <em>{c.journal}</em>{c.year ? `, ${c.year}` : ""}
                          </a>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                {result.furtherReading.length > 0 && (
                  <div className="border-t border-slate-100 dark:border-slate-700 pt-4">
                    <h4 className="text-xs font-mono font-semibold uppercase tracking-wider text-slate-400 mb-2">Further Reading</h4>
                    <div className="space-y-1">
                      {result.furtherReading.map((fr) => (
                        <a key={fr.url} href={fr.url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400">
                          <ExternalLink className="w-3 h-3 shrink-0" />{fr.label}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-slate-400 italic border-t border-slate-100 dark:border-slate-700 pt-3">{result.disclaimer}</p>
              </div>
            )}
          </>
        )}

        {/* ── Marketing Brief Tab ── */}
        {activeTab === "marketing" && (
          <>
            {mktLoading && (
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating marketing brief for "{submitted}"…
                </div>
                <Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-20 w-full" /><Skeleton className="h-20 w-full" />
              </div>
            )}
            {mktError && !mktLoading && (
              <div className="flex items-start gap-2 text-red-600 dark:text-red-400 text-sm p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /><span>{mktError}</span>
              </div>
            )}
            {mktResult && !mktLoading && (
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-slate-900 dark:text-white">{mktResult.term} — Marketing Brief</h3>
                  {mktResult.fromCache && <span className="text-[10px] font-mono text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full">from cache</span>}
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 dark:bg-slate-700/40 rounded-lg">
                    <p className="text-xs font-mono font-semibold uppercase tracking-wider text-slate-400 mb-2">Mechanism (plain-English)</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{mktResult.mechanism}</p>
                  </div>

                  <div className="p-4 bg-slate-50 dark:bg-slate-700/40 rounded-lg">
                    <p className="text-xs font-mono font-semibold uppercase tracking-wider text-slate-400 mb-2">Target Audience</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{mktResult.targetAudience}</p>
                  </div>

                  <div className="p-4 bg-slate-50 dark:bg-slate-700/40 rounded-lg">
                    <p className="text-xs font-mono font-semibold uppercase tracking-wider text-slate-400 mb-2">Positioning Angle</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{mktResult.positioningAngle}</p>
                  </div>

                  <div className="p-4 bg-slate-50 dark:bg-slate-700/40 rounded-lg">
                    <p className="text-xs font-mono font-semibold uppercase tracking-wider text-slate-400 mb-2">Demand Signal</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{mktResult.demandSignal}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="flex items-center gap-1.5 mb-3">
                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                        <p className="text-xs font-mono font-semibold uppercase tracking-wider text-green-700 dark:text-green-400">Compliant Claims</p>
                      </div>
                      <ul className="space-y-1.5">
                        {mktResult.compliantClaims.map((c, i) => (
                          <li key={i} className="text-sm text-green-800 dark:text-green-300 flex items-start gap-2">
                            <span className="text-green-500 shrink-0 mt-0.5">•</span>{c}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <div className="flex items-center gap-1.5 mb-3">
                        <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                        <p className="text-xs font-mono font-semibold uppercase tracking-wider text-red-700 dark:text-red-400">Avoid Claims</p>
                      </div>
                      <ul className="space-y-1.5">
                        {mktResult.avoidClaims.map((c, i) => (
                          <li key={i} className="text-sm text-red-800 dark:text-red-300 flex items-start gap-2">
                            <span className="text-red-500 shrink-0 mt-0.5">✕</span>{c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <p className="text-xs font-mono font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-400 mb-3">Competitor Messaging Themes</p>
                      <ul className="space-y-1.5">
                        {(mktResult.competitorMessagingThemes ?? []).map((t, i) => (
                          <li key={i} className="text-sm text-blue-800 dark:text-blue-300 flex items-start gap-2">
                            <span className="text-blue-400 shrink-0 mt-0.5">•</span>{t}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                      <p className="text-xs font-mono font-semibold uppercase tracking-wider text-purple-700 dark:text-purple-400 mb-3">Commonly Bundled With</p>
                      <ul className="space-y-1.5">
                        {(mktResult.commonlyBundledWith ?? []).map((b, i) => (
                          <li key={i} className="text-sm text-purple-800 dark:text-purple-300 flex items-start gap-2">
                            <span className="text-purple-400 shrink-0 mt-0.5">•</span>{b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {mktResult.strategicFit && (() => {
                  const sf = mktResult.strategicFit;
                  const ratingStyle = (r: StrategicRating) =>
                    r === "High"       ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400" :
                    r === "Medium"     ? "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400" :
                    r === "Low"        ? "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400" :
                                        "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400";

                  const dims: Array<{ label: string; dim: RatedDimension }> = [
                    { label: "Retention Play",   dim: sf.retentionPlay },
                    { label: "Acquisition Play", dim: sf.acquisitionPlay },
                    { label: "Higher AOV",        dim: sf.aovImpact },
                    { label: "Higher LTV",        dim: sf.ltvImpact },
                    { label: "Competitive Defense", dim: sf.competitiveDefense },
                    { label: "Brand Awareness",   dim: sf.brandAwareness },
                  ];

                  return (
                    <div className="space-y-4 pt-1">
                      <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
                        <p className="text-xs font-mono font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 mb-2">Patient Problem We're Solving</p>
                        <p className="text-sm text-indigo-900 dark:text-indigo-200 leading-relaxed">{sf.patientProblem}</p>
                      </div>

                      <div>
                        <p className="text-xs font-mono font-semibold uppercase tracking-wider text-slate-400 mb-3">Strategic Fit — GM Assessment</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {dims.map(({ label, dim }) => (
                            <div key={label} className="p-3 bg-slate-50 dark:bg-slate-700/40 rounded-lg space-y-1.5">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">{label}</span>
                                <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${ratingStyle(dim.rating)}`}>{dim.rating}</span>
                              </div>
                              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{dim.rationale}</p>
                            </div>
                          ))}
                        </div>

                        <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-700/40 rounded-lg space-y-1.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">New Customer Segment</span>
                            <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${ratingStyle(sf.newCustomerSegment.rating)}`}>{sf.newCustomerSegment.rating}</span>
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{sf.newCustomerSegment.rationale}</p>
                          {sf.newCustomerSegment.segmentDescription && (
                            <p className="text-xs text-slate-600 dark:text-slate-300 italic">{sf.newCustomerSegment.segmentDescription}</p>
                          )}
                        </div>
                      </div>

                      <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
                        <p className="text-xs font-mono font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 mb-2">Strategic Summary</p>
                        <p className="text-sm font-medium text-indigo-900 dark:text-indigo-200 leading-relaxed">{sf.strategicSummary}</p>
                      </div>
                    </div>
                  );
                })()}

                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-500" />
                  <p className="text-[11px] text-amber-700 dark:text-amber-400 leading-relaxed">
                    AI-generated drafting aid. All claims must be reviewed by a compliance officer before use in any marketing material. Not legal or regulatory advice.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Gate ─────────────────────────────────────────────────────────────────────
function GateScreen({ onKey }: { onKey: (k: string) => void }) {
  const [val, setVal] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!val.trim()) return;
    setChecking(true); setError("");
    try {
      const res = await fetch(`${API}/news`, { headers: { "x-peptide-key": val.trim() } });
      if (res.status === 401) { setError("Incorrect key. Try again."); }
      else { sessionStorage.setItem("peptide-key", val.trim()); onKey(val.trim()); }
    } catch { setError("Could not reach server. Try again."); }
    finally { setChecking(false); }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950">
      <div className="w-full max-w-sm p-8 bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl">
        <div className="flex items-center justify-center w-12 h-12 bg-slate-800 rounded-xl mb-6 mx-auto">
          <Lock className="w-6 h-6 text-slate-400" />
        </div>
        <h1 className="text-white font-mono text-lg font-bold text-center mb-1">Peptide Intel</h1>
        <p className="text-slate-400 text-sm text-center mb-6">Enter the access key to continue</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input type="password" value={val} onChange={(e) => setVal(e.target.value)}
            placeholder="Access key…" autoFocus
            className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-600 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-slate-400" />
          {error && <p className="text-red-400 text-xs">{error}</p>}
          <button type="submit" disabled={checking || !val.trim()}
            className="w-full py-2.5 rounded-lg bg-white text-slate-900 font-semibold text-sm hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed">
            {checking ? "Checking…" : "Enter"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── News ─────────────────────────────────────────────────────────────────────
function NewsSection({ pKey }: { pKey: string }) {
  const { data, isLoading, error } = usePeptideQuery<{ items: NewsSource[] }>("news", pKey, !!pKey);
  return (
    <Section title="News & Feeds" icon={Newspaper} isLoading={isLoading} error={error as Error}>
      {isLoading ? <div className="space-y-3">{[1, 2, 3, 4].map(i => <CardSkeleton key={i} />)}</div> : (
        <div className="space-y-4 max-h-[520px] overflow-y-auto pr-1">
          {(data?.items ?? []).map((src) => (
            <div key={src.source}>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{src.source}</p>
              <div className="space-y-1.5">
                {src.items.map((item) => (
                  <a key={item.link} href={item.link} target="_blank" rel="noopener noreferrer"
                    className="block p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 border border-transparent hover:border-slate-200 dark:hover:border-slate-600 transition-colors group">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200 leading-snug group-hover:text-blue-600 dark:group-hover:text-blue-400 line-clamp-2">{item.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{item.pubDate ? new Date(item.pubDate).toLocaleDateString() : ""}</p>
                    {item.summary && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{item.summary}</p>}
                  </a>
                ))}
              </div>
            </div>
          ))}
          {(!data?.items || data.items.length === 0) && <p className="text-sm text-slate-400 text-center py-4">No feed items found.</p>}
        </div>
      )}
    </Section>
  );
}

// ─── Research ─────────────────────────────────────────────────────────────────
function ResearchSection({ pKey }: { pKey: string }) {
  const { data, isLoading, error } = usePeptideQuery<{ papers: Paper[] }>("research", pKey, !!pKey);
  return (
    <Section title="PubMed Research" icon={FlaskConical} isLoading={isLoading} error={error as Error}>
      {isLoading ? <div className="space-y-3">{[1, 2, 3, 4, 5].map(i => <CardSkeleton key={i} />)}</div> : (
        <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
          {(data?.papers ?? []).map((paper) => (
            <a key={paper.id} href={paper.link} target="_blank" rel="noopener noreferrer"
              className="block p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 border border-transparent hover:border-slate-200 dark:hover:border-slate-600 transition-colors group">
              <span className="inline-block text-[10px] font-mono font-semibold uppercase tracking-wider bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded mb-1.5">{paper.term}</span>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-200 leading-snug group-hover:text-blue-600 dark:group-hover:text-blue-400 line-clamp-3">{paper.title}</p>
              <p className="text-xs text-slate-400 mt-1">{paper.journal} · {paper.pubDate}</p>
              {paper.authors && <p className="text-xs text-slate-400 line-clamp-1">{paper.authors}</p>}
            </a>
          ))}
          {(!data?.papers || data.papers.length === 0) && <p className="text-sm text-slate-400 text-center py-4">No papers found.</p>}
        </div>
      )}
    </Section>
  );
}

// ─── Podcasts ─────────────────────────────────────────────────────────────────
function PodcastsSection({ pKey }: { pKey: string }) {
  const { data, isLoading, error } = usePeptideQuery<{ videos: YoutubeChannel[]; error?: string }>("podcasts", pKey, !!pKey);
  return (
    <Section title="YouTube / Podcasts" icon={Mic2} isLoading={isLoading} error={error as Error}>
      {isLoading ? <div className="grid grid-cols-2 gap-3">{[1, 2, 3, 4].map(i => <CardSkeleton key={i} />)}</div>
        : data?.error ? (
          <div className="flex items-start gap-2 text-amber-600 dark:text-amber-400 text-sm p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{data.error} — add YOUTUBE_API_KEY to environment secrets.</span>
          </div>
        ) : (
          <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
            {(data?.videos ?? []).map((ch) => (
              <div key={ch.channel}>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{ch.channel}</p>
                <div className="space-y-2">
                  {ch.videos.map((v) => (
                    <a key={v.videoId} href={v.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 border border-transparent hover:border-slate-200 dark:hover:border-slate-600 transition-colors group">
                      {v.thumbnail ? (
                        <div className="relative shrink-0">
                          <img src={v.thumbnail} alt="" className="w-24 h-14 object-cover rounded" />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-6 h-6 bg-black/60 rounded-full flex items-center justify-center">
                              <Play className="w-3 h-3 text-white fill-white" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="w-24 h-14 bg-slate-100 dark:bg-slate-700 rounded shrink-0 flex items-center justify-center">
                          <Video className="w-5 h-5 text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 leading-snug group-hover:text-blue-600 dark:group-hover:text-blue-400 line-clamp-2">{v.title}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{new Date(v.publishedAt).toLocaleDateString()}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ))}
            {(!data?.videos || data.videos.length === 0) && <p className="text-sm text-slate-400 text-center py-4">No videos found.</p>}
          </div>
        )}
    </Section>
  );
}

// ─── Trends ───────────────────────────────────────────────────────────────────
const TRENDS_KEYWORDS = ["BPC 157", "peptides for recovery", "TB 500", "GHK Cu", "ipamorelin"];
function trendsEmbedUrl(keyword: string) {
  const req = encodeURIComponent(JSON.stringify({
    comparisonItem: [{ keyword, geo: "US", time: "today 12-m" }], category: 0, property: "",
  }));
  return `https://trends.google.com/trends/embed/explore/TIMESERIES?req=${req}&tz=-300&lang=en`;
}

function TrendsSection() {
  const [activeKw, setActiveKw] = useState(TRENDS_KEYWORDS[0]);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  function handleSelect(kw: string) {
    setActiveKw(kw); setLoaded(false); setFailed(false);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setFailed(true), 8000);
  }

  useEffect(() => {
    timerRef.current = setTimeout(() => setFailed(true), 8000);
    return () => clearTimeout(timerRef.current);
  }, [activeKw]);

  return (
    <Section title="Google Trends" icon={TrendingUp}>
      <div className="flex flex-wrap gap-2 mb-3">
        {TRENDS_KEYWORDS.map((kw) => (
          <button key={kw} onClick={() => handleSelect(kw)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${activeKw === kw ? "bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white" : "border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:border-slate-400"}`}>
            {kw}
          </button>
        ))}
      </div>
      {failed && !loaded ? (
        <div className="rounded-lg bg-slate-50 dark:bg-slate-700/40 p-4 text-center">
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">Trends widget couldn't load in this context.</p>
          <a href={`https://trends.google.com/trends/explore?q=${encodeURIComponent(activeKw)}&geo=US`} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
            View "{activeKw}" on Google Trends <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      ) : (
        <div className="relative rounded-lg overflow-hidden bg-slate-50 dark:bg-slate-700/20" style={{ height: 220 }}>
          {!loaded && <div className="absolute inset-0 flex items-center justify-center"><Skeleton className="w-full h-full" /></div>}
          <iframe key={activeKw} src={trendsEmbedUrl(activeKw)} width="100%" height="220" frameBorder="0" scrolling="no"
            onLoad={() => { setLoaded(true); clearTimeout(timerRef.current); }} onError={() => setFailed(true)}
            className={`w-full transition-opacity ${loaded ? "opacity-100" : "opacity-0"}`} />
        </div>
      )}
    </Section>
  );
}

// ─── TikTok ───────────────────────────────────────────────────────────────────
const TIKTOK_TERMS: Array<{ label: string; tag: string }> = [
  { label: "BPC-157",        tag: "bpc157" },
  { label: "TB-500",         tag: "tb500" },
  { label: "GHK-Cu",         tag: "ghkcu" },
  { label: "Ipamorelin",     tag: "ipamorelin" },
  { label: "CJC-1295",       tag: "cjc1295" },
  { label: "Semax",          tag: "semax" },
  { label: "Selank",         tag: "selank" },
  { label: "Epithalon",      tag: "epithalon" },
  { label: "Peptide Therapy",tag: "peptidetherapy" },
];

function TikTokSection() {
  return (
    <Section title="TikTok Search" icon={Play}>
      <div className="space-y-4">
        {/* Context note */}
        <div className="rounded-lg bg-slate-800/60 border border-slate-700 px-4 py-3 flex items-start gap-3">
          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white shrink-0 mt-0.5" aria-hidden>
            <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V9.05a8.16 8.16 0 004.77 1.52V7.12a4.85 4.85 0 01-1-.43z" />
          </svg>
          <p className="text-slate-300 text-xs leading-relaxed">
            TikTok doesn't expose search trend data via a public API. These links open TikTok search and hashtag pages directly in a new tab — the fastest way to see current volume and content for each compound.
          </p>
        </div>

        {/* Per-term cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {TIKTOK_TERMS.map(({ label, tag }) => (
            <div key={tag} className="rounded-lg bg-slate-800 border border-slate-700 hover:border-slate-500 transition-colors p-4">
              <p className="text-white font-semibold text-sm mb-3">{label}</p>
              <div className="flex gap-2">
                <a
                  href={`https://www.tiktok.com/search?q=${encodeURIComponent(label)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-center text-xs font-medium px-2 py-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-200 transition-colors"
                >
                  Search
                </a>
                <a
                  href={`https://www.tiktok.com/tag/${tag}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-center text-xs font-medium px-2 py-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-200 transition-colors"
                >
                  #{tag}
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Creative Center deep-links */}
        <div className="rounded-lg bg-slate-800 border border-slate-700 p-4">
          <p className="text-slate-300 text-xs font-medium uppercase tracking-wider mb-3">TikTok Creative Center</p>
          <div className="flex flex-wrap gap-2">
            <a href="https://ads.tiktok.com/business/creativecenter/keyword-insights/pc/en"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-200 transition-colors">
              Keyword Insights <ExternalLink className="w-3 h-3" />
            </a>
            <a href="https://ads.tiktok.com/business/creativecenter/trends/hashtags/pc/en"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-200 transition-colors">
              Trending Hashtags <ExternalLink className="w-3 h-3" />
            </a>
            <a href="https://ads.tiktok.com/business/creativecenter/trends/rising/pc/en"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-200 transition-colors">
              Rising Trends <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ─── Meta Ads ─────────────────────────────────────────────────────────────────
function MetaAdsSection() {
  const TERMS = ["BPC-157", "peptides", "TB-500", "GHK-Cu"];
  const metaUrl = (term: string) =>
    `https://www.facebook.com/ads/library/?active_status=all&ad_type=all&country=US&q=${encodeURIComponent(term)}&media_type=all`;
  return (
    <Section title="Meta Ad Library" icon={ExternalLink}>
      <div className="rounded-lg bg-gradient-to-br from-blue-900 to-blue-800 p-5 text-center mb-3">
        <p className="text-blue-100 text-xs mb-1 uppercase tracking-widest font-mono">Cannot be embedded (X-Frame-Options: DENY)</p>
        <p className="text-white text-sm mb-4">Opens Meta's Ad Library pre-filtered for peptide terms.</p>
        <div className="flex flex-wrap gap-2 justify-center">
          {TERMS.map((term) => (
            <a key={term} href={metaUrl(term)} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition-colors">
              "{term}" <ExternalLink className="w-3 h-3" />
            </a>
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-400 text-center">Each link opens Meta Ad Library filtered by that term in a new tab.</p>
    </Section>
  );
}

// ─── Daily Briefing ───────────────────────────────────────────────────────────
function DailyBriefingSection({ pKey }: { pKey: string }) {
  const [data, setData] = useState<BriefingResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchBriefing = useCallback(async () => {
    setIsLoading(true); setError("");
    try {
      const res = await fetch(`${API}/briefing`, { headers: { "x-peptide-key": pKey } });
      if (res.status === 401) { setError("Auth error."); return; }
      if (!res.ok) { setError(`Server error (${res.status})`); return; }
      setData((await res.json()) as BriefingResponse);
    } catch { setError("Network error."); }
    finally { setIsLoading(false); }
  }, [pKey]);

  useEffect(() => { if (pKey) fetchBriefing(); }, [pKey, fetchBriefing]);

  function renderBriefingLine(line: string) {
    const boldMatch = line.match(/^\*\*([^*]+)\*\*:?\s*(.*)/);
    if (boldMatch) {
      return <><strong className="text-slate-900 dark:text-white">{boldMatch[1]}:</strong> <span className="text-slate-600 dark:text-slate-300">{boldMatch[2]}</span></>;
    }
    return <span className="text-slate-600 dark:text-slate-300">{line}</span>;
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60">
        <Sparkles className="w-4 h-4 text-slate-500" />
        <h2 className="font-mono text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">Daily Briefing</h2>
        {data && (
          <span className="text-[10px] text-slate-400 font-mono ml-1">
            generated {new Date(data.generatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · refreshes every 6h
          </span>
        )}
        {isLoading && <RefreshCw className="w-3 h-3 text-slate-400 animate-spin ml-auto" />}
        {!isLoading && (
          <button onClick={fetchBriefing} className="ml-auto p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors" title="Regenerate briefing">
            <RefreshCw className="w-3 h-3 text-slate-400" />
          </button>
        )}
      </div>
      <div className="p-5">
        {isLoading && (
          <div className="space-y-3">
            {[1,2,3,4,5].map(i => <Skeleton key={i} className={`h-5 ${i % 2 === 0 ? "w-5/6" : "w-full"}`} />)}
          </div>
        )}
        {error && !isLoading && (
          <div className="flex items-start gap-2 text-amber-600 dark:text-amber-400 text-sm p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error} — Signal Tracker data must be polled at least once before briefing can generate.</span>
          </div>
        )}
        {data && !isLoading && (
          <ol className="space-y-3">
            {data.lines.map((line, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-[10px] font-mono text-slate-400 mt-0.5">{i + 1}</span>
                <p className="text-sm leading-relaxed">{renderBriefingLine(line)}</p>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

// ─── Regulatory & Compliance ──────────────────────────────────────────────────
function RegulatorySection({ pKey }: { pKey: string }) {
  const { data, isLoading, error } = usePeptideQuery<{ items: RegulatoryItem[] }>("regulatory", pKey, !!pKey);
  const items = data?.items ?? [];

  const badgeColor = (type: string) => {
    if (type === "fda") return "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800";
    if (type === "dea") return "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800";
    return "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-600";
  };

  return (
    <Section title="Regulatory & Compliance" icon={Shield} isLoading={isLoading} error={error as Error}>
      {isLoading ? <div className="space-y-2">{[1,2,3].map(i => <CardSkeleton key={i} />)}</div> : (
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
          {items.length === 0 && (
            <div className="text-center py-6 text-sm text-slate-400">
              <Shield className="w-6 h-6 mx-auto mb-2 text-slate-300" />
              No peptide-relevant regulatory items in latest feed cycle.
            </div>
          )}
          {items.map((item, i) => (
            <a key={i} href={item.link} target="_blank" rel="noopener noreferrer"
              className="block p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 border border-transparent hover:border-slate-200 dark:hover:border-slate-600 transition-colors group">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${badgeColor(item.type)}`}>
                  {item.source.split(" ")[0]}
                </span>
                {item.compounds.map((c) => (
                  <span key={c} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800">
                    {c}
                  </span>
                ))}
              </div>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-200 leading-snug group-hover:text-blue-600 dark:group-hover:text-blue-400 line-clamp-2">{item.title}</p>
              {item.pubDate && <p className="text-xs text-slate-400 mt-0.5">{new Date(item.pubDate).toLocaleDateString()}</p>}
            </a>
          ))}
        </div>
      )}
    </Section>
  );
}

// ─── Launch Readiness Scorecard ───────────────────────────────────────────────
function LaunchScorecardSection({ pKey }: { pKey: string }) {
  const { data, isLoading } = usePeptideQuery<{ rows: ScorecardRow[] }>("scorecard", pKey, !!pKey);
  const rows = data?.rows ?? [];

  const statusDot = (s: "green" | "yellow" | "red") => {
    const cls = s === "green" ? "bg-green-500" : s === "yellow" ? "bg-yellow-400" : "bg-red-500";
    return <span className={`inline-block w-2.5 h-2.5 rounded-full ${cls} shrink-0`} />;
  };

  const signalBar = (v: number | null) => {
    if (v === null) return <span className="text-slate-300 dark:text-slate-600 text-xs font-mono">—</span>;
    const pct = Math.round(v * 100);
    return (
      <div className="flex items-center gap-2">
        <div className="w-16 h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-[10px] font-mono text-slate-400">{pct}</span>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60">
        <ClipboardList className="w-4 h-4 text-slate-500" />
        <h2 className="font-mono text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">Launch Readiness Scorecard</h2>
        <span className="ml-auto text-[10px] text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full font-mono">research signal live · regulatory manual</span>
      </div>
      <div className="p-4 overflow-x-auto">
        {isLoading ? (
          <div className="space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-8 w-full" />)}</div>
        ) : (
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="border-b border-slate-100 dark:border-slate-700">
                <th className="text-left pb-2 pr-4 text-xs font-mono text-slate-400 uppercase tracking-wider">Compound</th>
                <th className="text-center pb-2 px-3 text-xs font-mono text-slate-400 uppercase tracking-wider">Research Signal</th>
                <th className="text-center pb-2 px-3 text-xs font-mono text-slate-400 uppercase tracking-wider">Regulatory</th>
                <th className="text-center pb-2 px-3 text-xs font-mono text-slate-400 uppercase tracking-wider">AgelessRx Live</th>
                <th className="text-left pb-2 pl-3 text-xs font-mono text-slate-400 uppercase tracking-wider">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 dark:divide-slate-700/50">
              {rows.map((row) => (
                <tr key={row.compound} className="group hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                  <td className="py-2.5 pr-4 font-medium text-slate-800 dark:text-slate-200 whitespace-nowrap">{row.compound}</td>
                  <td className="py-2.5 px-3 text-center">{signalBar(row.researchSignal)}</td>
                  <td className="py-2.5 px-3">
                    <div className="flex items-center justify-center gap-1.5">
                      {statusDot(row.regulatoryStatus)}
                      <span className="text-xs text-slate-500 dark:text-slate-400 capitalize">{row.regulatoryStatus}</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    {row.agelessrxLive
                      ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                      : <Minus className="w-4 h-4 text-slate-300 dark:text-slate-600 mx-auto" />}
                  </td>
                  <td className="py-2.5 pl-3 text-xs text-slate-400 dark:text-slate-500 max-w-[240px]">{row.regulatoryNote}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="text-[10px] text-slate-400 mt-3">
          Regulatory status is manually maintained in <code className="font-mono bg-slate-100 dark:bg-slate-700 px-1 rounded">peptides.config.ts</code> — update as guidance changes.
          Competitor ad saturation column pending Meta Ad Library API access.
        </p>
      </div>
    </div>
  );
}

// ─── Ad Intelligence Stub ─────────────────────────────────────────────────────
function AdIntelStub() {
  return (
    <Section title="Ad Intelligence" icon={BarChart}>
      <div className="rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-600 p-6 text-center">
        <BarChart className="w-8 h-8 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
        <p className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-1">Pending API Access</p>
        <p className="text-xs text-slate-400 dark:text-slate-500 leading-relaxed max-w-[240px] mx-auto">
          Real ad-creative intelligence (copy, images, date ranges) requires Meta Ad Library API developer approval.
          Layout reserved — wire in once access is granted.
        </p>
        <a href="https://www.facebook.com/ads/library/api/" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-3 text-xs text-blue-500 hover:text-blue-600 transition-colors">
          Apply for API access <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </Section>
  );
}

// ─── Page error boundary ──────────────────────────────────────────────────────
// A render error anywhere below unmounts ONLY this page's content and shows a
// readable message + retry instead of a blank white screen. The underlying
// bug must still be fixed — this is containment and diagnosis, not a fix: the
// message names the error so it can be reported, never swallowed silently.
class PageErrorBoundary extends React.Component<
  { onRetry: () => void; children: React.ReactNode },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface the component stack in the console — the thrown error alone
    // only carries minified frames.
    console.error("Peptide dashboard render error:", error, info.componentStack);
  }
  override render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-slate-100 dark:bg-slate-950 flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-xl border border-red-200 dark:border-red-900 p-6 text-center">
            <XCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
            <h1 className="font-mono text-sm font-bold text-slate-800 dark:text-slate-100 mb-2">
              This page hit an error while rendering
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
              The dashboard crashed instead of displaying. This is a bug worth reporting — the data may
              have arrived in an unexpected shape.
            </p>
            <p className="font-mono text-[11px] text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2 my-3 break-all">
              {this.state.error.message}
            </p>
            <button
              onClick={() => {
                this.setState({ error: null });
                this.props.onRetry();
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-medium rounded-lg hover:bg-slate-700 dark:hover:bg-slate-100 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Reload data and retry
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard({ pKey }: { pKey: string }) {
  const queryClient = useQueryClient();
  const [lastRefresh, setLastRefresh] = useState(new Date());

  function clearCache() {
    fetch(`${API}/cache/clear`, { method: "POST", headers: { "x-peptide-key": pKey } })
      .then(() => { queryClient.invalidateQueries({ queryKey: ["peptides"] }); setLastRefresh(new Date()); });
  }

  function forcePoll() {
    fetch(`${API}/trending/poll`, { method: "POST", headers: { "x-peptide-key": pKey } })
      .then(() => queryClient.invalidateQueries({ queryKey: ["peptides", "trending"] }));
  }

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950 p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-mono text-lg font-bold text-slate-900 dark:text-white tracking-tight">🧬 Peptide Intel Dashboard</h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Internal use only · Last refresh: {lastRefresh.toLocaleTimeString()} ·{" "}
            <span className="text-amber-500 font-medium">Not indexed</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={forcePoll}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg hover:border-slate-400 transition-colors">
            <Zap className="w-3 h-3" /> Poll Signals
          </button>
          <button onClick={clearCache}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg hover:border-slate-400 transition-colors">
            <RefreshCw className="w-3 h-3" /> Clear Cache
          </button>
          <span className="flex items-center gap-1 text-xs px-2 py-1 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg border border-red-100 dark:border-red-800">
            <Lock className="w-3 h-3" /> Private
          </span>
        </div>
      </div>

      {/* Daily Briefing (full width, first thing visible) */}
      <div className="mb-4">
        <DailyBriefingSection pKey={pKey} />
      </div>

      {/* Signal Tracker (full width) */}
      <div className="mb-4">
        <TrendingSection pKey={pKey} />
      </div>

      {/* Launch Readiness Scorecard (full width) */}
      <div className="mb-4">
        <LaunchScorecardSection pKey={pKey} />
      </div>

      {/* Peptide Wiki / Lookup (full width) */}
      <div className="mb-4">
        <LookupSection pKey={pKey} />
      </div>

      {/* Data sections grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        <div className="lg:col-span-1 xl:col-span-2"><NewsSection pKey={pKey} /></div>
        <div><ResearchSection pKey={pKey} /></div>
        <div><PodcastsSection pKey={pKey} /></div>
        <div><RegulatorySection pKey={pKey} /></div>
        <div><TrendsSection /></div>
        <div><TikTokSection /></div>
        <div><MetaAdsSection /></div>
        <div><AdIntelStub /></div>
      </div>

      <p className="mt-6 text-center text-xs text-slate-400">
        Signals polled every 30 min · Feed/keyword config in{" "}
        <code className="font-mono bg-slate-200 dark:bg-slate-700 px-1 py-0.5 rounded">
          artifacts/api-server/src/config/peptides.config.ts
        </code>
      </p>
    </div>
  );
}

// ─── Page entry ───────────────────────────────────────────────────────────────
function PeptideDashboardInner() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const urlKey = params.get("key") ?? "";

  const [pKey, setPKey] = useState<string>(() => urlKey || sessionStorage.getItem("peptide-key") || "");

  useEffect(() => {
    if (urlKey) { sessionStorage.setItem("peptide-key", urlKey); setPKey(urlKey); }
  }, [urlKey]);

  if (!pKey) return <GateScreen onKey={setPKey} />;

  return (
    <>
      <title>Peptide Intel – Internal Dashboard</title>
      <meta name="robots" content="noindex, nofollow" />
      <Dashboard pKey={pKey} />
    </>
  );
}

export default function PeptideDashboard() {
  const queryClient = useQueryClient();
  return (
    <PageErrorBoundary
      onRetry={() => {
        // RESET (not just invalidate) so the cached payload that crashed the
        // render is gone before the boundary remounts children — otherwise
        // they synchronously re-render the same bad data and crash again
        // before the refetch resolves. Reset puts sections back into their
        // loading state while fresh data arrives.
        queryClient.resetQueries({ queryKey: ["peptides"] });
      }}
    >
      <PeptideDashboardInner />
    </PageErrorBoundary>
  );
}
